import React from 'react';

const IMAGES = [
  {
    src: '/legal/signup-proof-unchecked.jpg',
    alt: 'LISTING AiGENT SMS opt-in modal — step 1, consent checkbox not checked.',
    caption: (
      <>
        <strong>Step 1 &mdash; Opt-in modal (default state).</strong> Immediately after a user
        signs in, LISTING AiGENT displays a modal offering optional SMS notifications.
        The <em>&ldquo;I agree to receive text messages&rdquo;</em> checkbox is <strong>unchecked
        by default</strong>. The user must (1) enter their mobile number, (2) tick the
        consent box, and only then can (3) tap &ldquo;Send verification code.&rdquo; The
        &ldquo;Send verification code&rdquo; button remains disabled until both the number
        and checkbox are filled. A visible &ldquo;Not now&rdquo; button lets the user dismiss
        the modal without providing consent; account access is <strong>not</strong>
        affected.
      </>
    ),
  },
  {
    src: '/legal/signup-proof-checked.jpg',
    alt: 'LISTING AiGENT SMS opt-in modal — step 1, consent checkbox checked and phone entered.',
    caption: (
      <>
        <strong>Step 1 &mdash; Opt-in modal (consent given).</strong> The user has entered
        their mobile number and voluntarily ticked the consent checkbox. Directly next to
        the checkbox the full disclosure is shown to the user before they submit, with
        clickable links to both policies:
        <em> &ldquo;I agree to receive text messages (SMS) from LISTING AiGENT, including
        security verification codes, listing details, and appointment reminders, at the
        number I provide. Msg &amp; data rates may apply. Msg frequency varies. Reply
        STOP to unsubscribe, HELP for help. View SMS Policy. View Privacy Policy.&rdquo;</em>
      </>
    ),
  },
  {
    src: '/legal/signup-proof-settings.jpg',
    alt: 'LISTING AiGENT Settings page — SMS opt-in panel with identical consent disclosure.',
    caption: (
      <>
        <strong>Step 2 &mdash; Settings page (secondary opt-in path).</strong> Any user who
        dismissed the modal can enable SMS later from <code>/settings</code>. The identical
        consent language and unchecked-by-default checkbox appear here, followed by the same
        Twilio Verify one-time-code confirmation. Users can also opt <em>out</em> from this
        page at any time; status changes are stored to a permanent audit log.
      </>
    ),
  },
];

export default function SignupProof() {
  return (
    <div className="bg-brand-cream min-h-screen">
      <section className="bg-brand-green text-brand-cream py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Twilio A2P 10DLC &middot; Opt-in proof</span>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">
            SMS Opt-in Proof &amp; Consent Flow
          </h1>
          <p className="mt-4 text-brand-cream/80 max-w-2xl">
            This page documents every path a LISTING AiGENT user can take to opt in to
            SMS messaging, the disclosures shown at each step, and the audit-log record
            we retain. It is intended for Twilio&rsquo;s A2P 10DLC campaign reviewers.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 space-y-6">
          <div className="bg-white rounded-2xl border border-brand-cream-dark p-6 md:p-8">
            <h2 className="font-serif text-2xl text-brand-green mb-3">Business &amp; use case</h2>
            <dl className="text-sm text-brand-green/85 grid sm:grid-cols-3 gap-y-2 gap-x-6">
              <dt className="font-semibold text-brand-green">Business name</dt>
              <dd className="sm:col-span-2">LISTING AiGENT LLC</dd>
              <dt className="font-semibold text-brand-green">Website</dt>
              <dd className="sm:col-span-2"><a className="underline" href="https://listingaigent.com">https://listingaigent.com</a></dd>
              <dt className="font-semibold text-brand-green">Product</dt>
              <dd className="sm:col-span-2">AI assistant for licensed real-estate agents (mobile app + website).</dd>
              <dt className="font-semibold text-brand-green">Use case</dt>
              <dd className="sm:col-span-2">Account notifications, appointment reminders, and lead alerts that the account holder has explicitly opted in to receive.</dd>
              <dt className="font-semibold text-brand-green">Message frequency</dt>
              <dd className="sm:col-span-2">Variable &mdash; up to ~10 messages per week per opted-in user.</dd>
              <dt className="font-semibold text-brand-green">Opt-out keywords</dt>
              <dd className="sm:col-span-2"><code>STOP</code>, <code>STOPALL</code>, <code>UNSUBSCRIBE</code>, <code>CANCEL</code>, <code>END</code>, <code>QUIT</code></dd>
              <dt className="font-semibold text-brand-green">Help keyword</dt>
              <dd className="sm:col-span-2"><code>HELP</code> &mdash; auto-reply provides support contact + link to <a className="underline" href="/terms#s8">/terms#s8</a> (SMS Policy)</dd>
              <dt className="font-semibold text-brand-green">Message content</dt>
              <dd className="sm:col-span-2">Transactional only; no promotions to non-opted-in numbers.</dd>
            </dl>
          </div>

          <div className="bg-white rounded-2xl border border-brand-cream-dark p-6 md:p-8">
            <h2 className="font-serif text-2xl text-brand-green mb-3">Consent flow &mdash; end-to-end</h2>
            <ol className="list-decimal list-outside ml-5 space-y-3 text-sm text-brand-green/85">
              <li>
                <strong>Account creation.</strong> A user creates an account at
                <a className="underline" href="/signup"> /signup</a> using an email + password
                only. The signup form <strong>does not</strong> collect a phone number and
                <strong> does not</strong> imply any SMS consent. SMS opt-in is fully separate.
              </li>
              <li>
                <strong>Opt-in modal (primary path).</strong> After successful sign-in the
                app renders a modal (see screenshot #1 below) offering optional SMS
                notifications. The user must actively (a) enter their mobile number,
                (b) tick a consent checkbox that is unchecked by default, and (c) click
                &ldquo;Send verification code.&rdquo; A visible &ldquo;Not now&rdquo; button
                dismisses the modal without affecting account access. The modal only
                appears once per fresh sign-in and never for users who previously
                dismissed, opted in, or opted out.
              </li>
              <li>
                <strong>Twilio Verify confirmation.</strong> On submission the backend
                calls Twilio&rsquo;s Verify API to send a one-time code to the phone number.
                The number is stored only as <code>phone_pending</code> at this stage &mdash; no
                opt-in is recorded yet.
              </li>
              <li>
                <strong>Code entry.</strong> The modal advances to a code-entry step. The
                user must enter the 6-digit code received via SMS. Only after successful
                verification does the backend flip <code>sms_opted_in</code> to <code>true</code>,
                move the phone to the verified field, and append a permanent record to the
                user&rsquo;s <code>consent_history</code>.
              </li>
              <li>
                <strong>Settings page (secondary path).</strong> Users who dismissed the modal
                can enable SMS later at <a className="underline" href="/settings">/settings</a>.
                The same identical disclosure text and unchecked-by-default checkbox are
                shown (see screenshot #3). The verification and audit logging are identical.
              </li>
              <li>
                <strong>Opt-out.</strong> Users can text any recognised opt-out keyword to our
                Twilio number or click &ldquo;Turn off SMS&rdquo; on <code>/settings</code>.
                Opt-out is honoured immediately, <code>sms_opt_in_status</code> flips to
                <code> opted_out</code>, and a permanent record is added to the audit log.
              </li>
            </ol>
          </div>

          <div className="bg-white rounded-2xl border border-brand-cream-dark p-6 md:p-8">
            <h2 className="font-serif text-2xl text-brand-green mb-3">Required disclosures (verbatim)</h2>
            <p className="text-sm text-brand-green/85 mb-3">
              The following disclosure is displayed <strong>next to the consent checkbox</strong> in
              both the opt-in modal and the Settings page. It is presented in the user&rsquo;s language
              of choice and identical across both flows:
            </p>
            <blockquote className="bg-brand-cream border-l-4 border-brand-gold px-5 py-4 text-sm text-brand-green italic">
              &ldquo;I agree to receive text messages (SMS) from LISTING AiGENT, including
              security verification codes, listing details, and appointment reminders, at the
              number I provide. Msg &amp; data rates may apply. Msg frequency varies. Reply
              STOP to unsubscribe, HELP for help. View SMS Policy. View Privacy Policy.&rdquo;
            </blockquote>
            <p className="text-sm text-brand-green/85 mt-4">
              The disclosure includes two clickable links:
              {' '}<a className="underline" href="/terms#s8">SMS Policy</a> (merged into
              Section 8 of our Terms of Service) and
              {' '}<a className="underline" href="/privacy">Privacy Policy</a>. Both are
              opened in a new tab so the user does not lose their opt-in progress.
            </p>
            <p className="text-sm text-brand-green/85 mt-4">
              Full policy documents:
              {' '}<a className="underline" href="/terms#s8">SMS Policy (Terms &sect;8)</a>,
              {' '}<a className="underline" href="/privacy">Privacy Policy</a>,
              {' '}<a className="underline" href="/terms">Terms of Service</a>.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-brand-cream-dark p-6 md:p-8">
            <h2 className="font-serif text-2xl text-brand-green mb-3">Audit log we retain per user</h2>
            <p className="text-sm text-brand-green/85 mb-3">
              Every opt-in and opt-out event is appended to the user&rsquo;s
              <code> consent_history</code> collection with the following fields:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-1 text-sm text-brand-green/85">
              <li><code>text_version</code> &mdash; version identifier of the disclosure shown</li>
              <li><code>text</code> &mdash; the exact disclosure text shown to the user</li>
              <li><code>granted</code> &mdash; <code>true</code> for opt-in, <code>false</code> for opt-out</li>
              <li><code>granted_at</code> &mdash; ISO-8601 timestamp (UTC)</li>
              <li><code>ip</code> &mdash; client IP address at time of consent</li>
              <li><code>user_agent</code> &mdash; browser / device user-agent string</li>
              <li><code>source</code> &mdash; <code>modal</code>, <code>settings</code>, <code>sms-stop</code>, etc.</li>
              <li><code>phone</code> &mdash; the E.164-normalised number the consent applies to</li>
            </ul>
          </div>

          {IMAGES.map(({ src, alt, caption }) => (
            <figure key={src} className="bg-white rounded-2xl border border-brand-cream-dark overflow-hidden">
              <img src={src} alt={alt} className="w-full h-auto block" />
              <figcaption className="p-6 text-sm text-brand-green/85 leading-relaxed">
                {caption}
              </figcaption>
            </figure>
          ))}

          <div className="text-sm text-brand-green/75">
            Questions about this campaign? Email
            {' '}<a className="underline" href="mailto:contact@listingaigent.com">contact@listingaigent.com</a>.
          </div>
        </div>
      </section>
    </div>
  );
}
